import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Clock, 
  Film, 
  Tv, 
  Calendar,
  TrendingUp,
  Award,
  Play
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getUserStats } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const COLORS = ['#6366f1', '#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe', '#ede9fe', '#f5f3ff'];

const StatCard = ({ icon: Icon, label, value, subValue, gradient }) => (
  <div className={`stat-card relative overflow-hidden ${gradient || ''}`}>
    <div className="relative z-10">
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 rounded-lg bg-white/10">
          <Icon className="w-5 h-5 text-indigo-400" />
        </div>
        <span className="text-gray-400 text-sm">{label}</span>
      </div>
      <p className="text-3xl md:text-4xl font-display font-bold mb-1">{value}</p>
      {subValue && <p className="text-sm text-gray-500">{subValue}</p>}
    </div>
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="glass-card px-4 py-2">
        <p className="text-sm text-white">{label}</p>
        <p className="text-sm text-indigo-400 font-semibold">
          {payload[0].value} {payload[0].name === 'movies' ? 'films' : 'afleveringen'}
        </p>
      </div>
    );
  }
  return null;
};

export const StatsPage = () => {
  const { isAuthenticated } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }

      try {
        const data = await getUserStats();
        setStats(data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <div data-testid="stats-page" className="min-h-screen p-6 md:p-12">
        <div className="max-w-2xl mx-auto text-center py-20">
          <TrendingUp className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h1 className="text-2xl font-display font-bold mb-4">Mijn Statistieken</h1>
          <p className="text-gray-400 mb-8">Log in om je kijkstatistieken te bekijken.</p>
          <Link to="/login">
            <Button className="bg-indigo-600 hover:bg-indigo-700 rounded-full px-8 py-6">
              Inloggen
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const formatTime = (minutes) => {
    if (!minutes) return '0u';
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) {
      return `${days}d ${hours % 24}u`;
    }
    return `${hours}u ${minutes % 60}m`;
  };

  return (
    <div data-testid="stats-page" className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 gradient-wrapped opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#050505]" />
        
        <div className="relative p-6 md:p-12 pt-12 md:pt-20">
          <div className="max-w-4xl mx-auto text-center">
            <span className="text-xs uppercase tracking-[0.2em] text-indigo-400 font-medium mb-4 block">
              Jouw kijkjaar
            </span>
            <h1 className="text-4xl md:text-6xl font-display font-bold tracking-tight mb-4">
              Episo Wrapped
            </h1>
            <p className="text-gray-400 text-lg mb-8">
              Je persoonlijke kijkstatistieken
            </p>

            {/* Big Stat */}
            <div className="inline-block glass-card p-8 md:p-12 mb-8">
              <Clock className="w-12 h-12 text-indigo-400 mx-auto mb-4" />
              <p className="text-5xl md:text-7xl font-display font-bold text-gradient mb-2">
                {stats?.total_watch_time_hours || 0}
              </p>
              <p className="text-gray-400">uur gekeken</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Grid */}
      <section className="p-6 md:p-12 -mt-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <StatCard 
            icon={Film}
            label="Films Gekeken"
            value={stats?.total_movies_watched || 0}
            subValue={formatTime(stats?.total_movie_time_minutes)}
          />
          <StatCard 
            icon={Tv}
            label="Afleveringen"
            value={stats?.total_episodes_watched || 0}
            subValue={formatTime(stats?.total_series_time_minutes)}
          />
          <StatCard 
            icon={Play}
            label="Series"
            value={stats?.total_series || 0}
          />
          <StatCard 
            icon={Calendar}
            label="Dagen Gekeken"
            value={stats?.total_watch_time_days || 0}
            subValue="totale kijktijd"
          />
        </div>

        {/* Genre Breakdown */}
        {stats?.genre_breakdown && stats.genre_breakdown.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-display font-bold mb-6">Genre Breakdown</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Pie Chart */}
              <div className="glass-card p-6">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={stats.genre_breakdown}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="count"
                      nameKey="genre"
                    >
                      {stats.genre_breakdown.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="glass-card px-3 py-2">
                            <p className="text-sm">{payload[0].name}</p>
                            <p className="text-indigo-400 font-semibold">{payload[0].value}</p>
                          </div>
                        );
                      }
                      return null;
                    }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Genre List */}
              <div className="glass-card p-6">
                <div className="space-y-4">
                  {stats.genre_breakdown.slice(0, 7).map((genre, index) => (
                    <div key={genre.genre} className="flex items-center gap-4">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="flex-1 text-gray-300">{genre.genre}</span>
                      <span className="font-semibold">{genre.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Monthly Activity */}
        {stats?.monthly_data && stats.monthly_data.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-display font-bold mb-6">Maandelijkse Activiteit</h2>
            <div className="glass-card p-6">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats.monthly_data}>
                  <XAxis 
                    dataKey="month" 
                    stroke="#6b7280"
                    fontSize={12}
                    tickLine={false}
                  />
                  <YAxis 
                    stroke="#6b7280"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar 
                    dataKey="movies" 
                    fill="#6366f1" 
                    radius={[4, 4, 0, 0]}
                    name="movies"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Watchlist Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="glass-card p-6">
            <div className="flex items-center gap-3 mb-4">
              <Award className="w-6 h-6 text-yellow-500" />
              <h3 className="text-lg font-display font-semibold">Op je Watchlist</h3>
            </div>
            <div className="flex items-baseline gap-4">
              <div>
                <p className="text-3xl font-display font-bold">{stats?.watchlist_movies || 0}</p>
                <p className="text-sm text-gray-400">films</p>
              </div>
              <div>
                <p className="text-3xl font-display font-bold">{stats?.watchlist_series || 0}</p>
                <p className="text-sm text-gray-400">series</p>
              </div>
            </div>
          </div>

          <div className="glass-card p-6">
            <div className="flex items-center gap-3 mb-4">
              <TrendingUp className="w-6 h-6 text-green-500" />
              <h3 className="text-lg font-display font-semibold">Totale Kijktijd</h3>
            </div>
            <p className="text-3xl font-display font-bold mb-1">
              {formatTime(stats?.total_watch_time_minutes)}
            </p>
            <p className="text-sm text-gray-400">
              {stats?.total_movies_watched || 0} films + {stats?.total_episodes_watched || 0} afleveringen
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StatsPage;
